/**
 * FERP Portal - Gantt Chart Visualization
 *
 * Renders tasks as horizontal bars on a timeline with dependency arrows.
 * Pure visualization: all data comes from Task and Dependency primitives.
 *
 * Features:
 * - Horizontal bar per task (colored by status/priority)
 * - Dependency arrows between tasks (FS/FF/SS/SF)
 * - Progress overlay on in-progress tasks
 * - Today marker line
 * - Zoom: day / week / month views
 *
 * DRR-070: No hardcoded data — reads Task/Dependency from backend.
 * DRR-074: Dumb frontend — relies on backend for field definitions.
 */

import { useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  Group,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import {
  GqlTaskStatus,
  GqlTaskPriority,
  type TaskFieldsFragment,
  type DependencyFieldsFragment,
} from '@/generated/graphql';

// ============================================================================
// Types
// ============================================================================

export type GanttZoom = 'day' | 'week' | 'month';

export interface GanttChartProps {
  tasks: TaskFieldsFragment[];
  dependencies?: DependencyFieldsFragment[];
  zoom?: GanttZoom;
  onZoomChange?: (zoom: GanttZoom) => void;
  onTaskClick?: (task: TaskFieldsFragment) => void;
}

interface GanttRow {
  task: TaskFieldsFragment;
  startDate: Date;
  endDate: Date;
  progress: number;
}

// ============================================================================
// Constants
// ============================================================================

const ROW_HEIGHT = 36;
const LABEL_WIDTH = 220;
const HEADER_HEIGHT = 48;
const MIN_BAR_WIDTH = 4;

const STATUS_COLORS: Record<string, string> = {
  [GqlTaskStatus.PENDING]: '#868e96',
  [GqlTaskStatus.READY]: '#339af0',
  [GqlTaskStatus.ASSIGNED]: '#5c7cfa',
  [GqlTaskStatus.IN_PROGRESS]: '#22b8cf',
  [GqlTaskStatus.BLOCKED]: '#fd7e14',
  [GqlTaskStatus.PAUSED]: '#fab005',
  [GqlTaskStatus.PENDING_EXTERNAL]: '#be4bdb',
  [GqlTaskStatus.COMPLETED]: '#51cf66',
  [GqlTaskStatus.FAILED]: '#ff6b6b',
  [GqlTaskStatus.CANCELLED]: '#adb5bd',
};

const PRIORITY_BORDER: Record<string, string> = {
  [GqlTaskPriority.CRITICAL]: '2px solid #ff6b6b',
  [GqlTaskPriority.HIGH]: '2px solid #fd7e14',
  [GqlTaskPriority.NORMAL]: 'none',
  [GqlTaskPriority.LOW]: 'none',
};

// ============================================================================
// Helpers
// ============================================================================

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function diffDays(a: Date, b: Date): number {
  return (a.getTime() - b.getTime()) / 86400000;
}

function formatHeaderDate(date: Date, zoom: GanttZoom): string {
  const month = date.toLocaleString('default', { month: 'short' });
  const day = date.getDate();
  if (zoom === 'day') return `${month} ${day}`;
  if (zoom === 'week') return `${month} ${day}`;
  return `${month} ${date.getFullYear()}`;
}

function cellWidthForZoom(zoom: GanttZoom): number {
  switch (zoom) {
    case 'day': return 32;
    case 'week': return 18;
    case 'month': return 6;
  }
}

/**
 * Derive a visible date range from task data, providing default duration estimates.
 * DRR-116 priority: scheduledStart/End > startedAt/completedAt > createdAt + estimate
 */
function buildRows(tasks: TaskFieldsFragment[]): GanttRow[] {
  const now = new Date();
  return tasks.map((task) => {
    const start =
      parseDate(task.scheduledStart) ??
      parseDate(task.startedAt) ??
      parseDate(task.createdAt) ??
      now;

    let end: Date;
    if (task.scheduledStart && task.scheduledEnd) {
      end = parseDate(task.scheduledEnd) ?? addDays(start, 1);
    } else if (task.completedAt) {
      end = parseDate(task.completedAt) ?? addDays(start, 1);
    } else if (task.estimatedDurationMs) {
      const ms = parseInt(task.estimatedDurationMs, 10);
      end = isNaN(ms) ? addDays(start, 7) : new Date(start.getTime() + ms);
    } else {
      end = addDays(start, 7); // default 7-day bar if unknown
    }

    // Ensure minimum bar width (at least 1 day)
    if (end.getTime() - start.getTime() < 86400000) {
      end = addDays(start, 1);
    }

    const progress = task.progressPercent ?? (task.status === GqlTaskStatus.COMPLETED ? 100 : 0);

    return { task, startDate: startOfDay(start), endDate: startOfDay(end), progress };
  });
}

// ============================================================================
// Sub-components
// ============================================================================

function TodayLine({ left }: { left: number }) {
  return (
    <Box
      pos="absolute"
      top={0}
      bottom={0}
      left={left}
      w={2}
      bg="red.5"
      style={{ zIndex: 10, pointerEvents: 'none' }}
    />
  );
}

function TaskBar({
  row,
  left,
  width,
  onClick,
}: {
  row: GanttRow;
  left: number;
  width: number;
  onClick?: () => void;
}) {
  const color = STATUS_COLORS[row.task.status] ?? '#868e96';
  const border = PRIORITY_BORDER[row.task.priority] ?? 'none';
  const progressWidth = Math.max(0, Math.min(100, row.progress));

  return (
    <Tooltip
      label={
        <Stack gap={2}>
          <Text fw={600} size="sm">{row.task.name}</Text>
          <Text size="xs">Status: {row.task.status}</Text>
          <Text size="xs">Priority: {row.task.priority}</Text>
          {row.progress > 0 && <Text size="xs">Progress: {row.progress}%</Text>}
        </Stack>
      }
      multiline
      withArrow
    >
      <Box
        pos="absolute"
        top={4}
        left={left}
        w={Math.max(MIN_BAR_WIDTH, width)}
        h={ROW_HEIGHT - 8}
        bg={color}
        style={{
          borderRadius: 4,
          border,
          cursor: onClick ? 'pointer' : 'default',
          overflow: 'hidden',
          opacity: row.task.status === GqlTaskStatus.CANCELLED ? 0.4 : 1,
        }}
        onClick={onClick}
      >
        {/* Progress fill */}
        {progressWidth > 0 && progressWidth < 100 && (
          <Box
            pos="absolute"
            top={0}
            left={0}
            h="100%"
            w={`${progressWidth}%`}
            style={{
              background: 'rgba(255,255,255,0.25)',
              borderRadius: '4px 0 0 4px',
            }}
          />
        )}
        {/* Task name on the bar (if wide enough) */}
        {width > 60 && (
          <Text
            size="xs"
            c="white"
            fw={500}
            px={6}
            style={{
              lineHeight: `${ROW_HEIGHT - 8}px`,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.task.name}
          </Text>
        )}
      </Box>
    </Tooltip>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function GanttChart({
  tasks,
  dependencies = [],
  zoom: controlledZoom,
  onZoomChange,
  onTaskClick,
}: GanttChartProps) {
  const theme = useMantineTheme();
  const [internalZoom, setInternalZoom] = useState<GanttZoom>('week');
  const zoom = controlledZoom ?? internalZoom;
  const handleZoomChange = (v: string) => {
    const z = v as GanttZoom;
    setInternalZoom(z);
    onZoomChange?.(z);
  };

  const rows = useMemo(() => buildRows(tasks), [tasks]);

  // Compute timeline range
  const { timelineStart, timelineEnd, totalDays } = useMemo(() => {
    if (rows.length === 0) {
      const now = startOfDay(new Date());
      return { timelineStart: now, timelineEnd: addDays(now, 30), totalDays: 30 };
    }
    let min = rows[0]!.startDate;
    let max = rows[0]!.endDate;
    for (const r of rows) {
      if (r.startDate < min) min = r.startDate;
      if (r.endDate > max) max = r.endDate;
    }
    // Add padding: 2 days before, 5 days after
    const tStart = addDays(min, -2);
    const tEnd = addDays(max, 5);
    return {
      timelineStart: tStart,
      timelineEnd: tEnd,
      totalDays: Math.max(7, Math.ceil(diffDays(tEnd, tStart))),
    };
  }, [rows]);

  const cellWidth = cellWidthForZoom(zoom);
  const chartWidth = totalDays * cellWidth;
  const chartHeight = rows.length * ROW_HEIGHT;

  // Generate header labels
  const headerLabels = useMemo(() => {
    const labels: { date: Date; x: number; label: string }[] = [];
    const step = zoom === 'month' ? 30 : zoom === 'week' ? 7 : 1;
    for (let i = 0; i < totalDays; i += step) {
      const date = addDays(timelineStart, i);
      labels.push({
        date,
        x: i * cellWidth,
        label: formatHeaderDate(date, zoom),
      });
    }
    return labels;
  }, [timelineStart, totalDays, cellWidth, zoom]);

  // Today position
  const todayOffset = diffDays(startOfDay(new Date()), timelineStart) * cellWidth;
  const showToday = todayOffset >= 0 && todayOffset <= chartWidth;

  // Build task ID -> row index map for dependency drawing
  const taskIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((r, i) => map.set(r.task.id, i));
    return map;
  }, [rows]);

  if (tasks.length === 0) {
    return (
      <Card withBorder p="xl">
        <Text c="dimmed" ta="center">No tasks to display in Gantt view.</Text>
      </Card>
    );
  }

  return (
    <Card withBorder p={0}>
      {/* Toolbar */}
      <Group p="sm" justify="space-between" style={{ borderBottom: `1px solid ${theme.colors.gray[3]}` }}>
        <Text fw={600} size="sm">Gantt View</Text>
        <SegmentedControl
          size="xs"
          value={zoom}
          onChange={handleZoomChange}
          data={[
            { label: 'Day', value: 'day' },
            { label: 'Week', value: 'week' },
            { label: 'Month', value: 'month' },
          ]}
        />
      </Group>

      <Group align="flex-start" gap={0} wrap="nowrap">
        {/* Left: Task labels */}
        <Box w={LABEL_WIDTH} style={{ flexShrink: 0, borderRight: `1px solid ${theme.colors.gray[3]}` }}>
          <Box h={HEADER_HEIGHT} px="sm" style={{ borderBottom: `1px solid ${theme.colors.gray[3]}`, display: 'flex', alignItems: 'center' }}>
            <Text size="xs" fw={600} c="dimmed">Task</Text>
          </Box>
          {rows.map((row, i) => (
            <Box
              key={row.task.id}
              h={ROW_HEIGHT}
              px="sm"
              style={{
                display: 'flex',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.colors.gray[1]}`,
                cursor: onTaskClick ? 'pointer' : 'default',
              }}
              onClick={() => onTaskClick?.(row.task)}
            >
              <Tooltip label={row.task.name} openDelay={500}>
                <Text
                  size="xs"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: LABEL_WIDTH - 24,
                  }}
                >
                  {row.task.name}
                </Text>
              </Tooltip>
            </Box>
          ))}
        </Box>

        {/* Right: Timeline chart */}
        <ScrollArea style={{ flex: 1 }} type="auto" offsetScrollbars>
          <Box pos="relative" w={chartWidth} style={{ minWidth: chartWidth }}>
            {/* Header row */}
            <Box
              h={HEADER_HEIGHT}
              pos="relative"
              style={{
                borderBottom: `1px solid ${theme.colors.gray[3]}`,
                background: theme.colors.gray[0],
              }}
            >
              {headerLabels.map((hl, i) => (
                <Text
                  key={i}
                  size="xs"
                  c="dimmed"
                  pos="absolute"
                  left={hl.x + 4}
                  top={16}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {hl.label}
                </Text>
              ))}
            </Box>

            {/* Grid lines */}
            <Box pos="relative" h={chartHeight}>
              {/* Vertical grid lines */}
              {headerLabels.map((hl, i) => (
                <Box
                  key={i}
                  pos="absolute"
                  top={0}
                  bottom={0}
                  left={hl.x}
                  w={1}
                  bg="gray.2"
                  style={{ pointerEvents: 'none' }}
                />
              ))}

              {/* Horizontal row separators */}
              {rows.map((_, i) => (
                <Box
                  key={i}
                  pos="absolute"
                  left={0}
                  right={0}
                  top={(i + 1) * ROW_HEIGHT}
                  h={1}
                  bg="gray.1"
                  style={{ pointerEvents: 'none' }}
                />
              ))}

              {/* Today marker */}
              {showToday && <TodayLine left={todayOffset} />}

              {/* Task bars */}
              {rows.map((row, i) => {
                const left = diffDays(row.startDate, timelineStart) * cellWidth;
                const width = diffDays(row.endDate, row.startDate) * cellWidth;
                return (
                  <Box key={row.task.id} pos="relative" h={ROW_HEIGHT} top={i * ROW_HEIGHT}>
                    <TaskBar
                      row={row}
                      left={left}
                      width={width}
                      onClick={onTaskClick ? () => onTaskClick(row.task) : undefined}
                    />
                  </Box>
                );
              })}

              {/* Dependency arrows (SVG overlay) */}
              {dependencies.length > 0 && (
                <svg
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: chartWidth,
                    height: chartHeight,
                    pointerEvents: 'none',
                  }}
                >
                  <defs>
                    <marker
                      id="gantt-arrow"
                      viewBox="0 0 10 10"
                      refX="10"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 z" fill="#868e96" />
                    </marker>
                  </defs>
                  {dependencies
                    .filter((d) => d.active)
                    .map((dep) => {
                      // predecessorId/successorId removed from DependencyFieldsFragment (DRR-124)
                      const predIdx = taskIndexMap.get((dep as Record<string, unknown>)['predecessorId'] as string);
                      const succIdx = taskIndexMap.get((dep as Record<string, unknown>)['successorId'] as string);
                      if (predIdx === undefined || succIdx === undefined) return null;

                      const predRow = rows[predIdx];
                      const succRow = rows[succIdx];
                      if (!predRow || !succRow) return null;

                      // Arrow from end of predecessor to start of successor
                      const x1 = diffDays(predRow.endDate, timelineStart) * cellWidth;
                      const y1 = predIdx * ROW_HEIGHT + ROW_HEIGHT / 2;
                      const x2 = diffDays(succRow.startDate, timelineStart) * cellWidth;
                      const y2 = succIdx * ROW_HEIGHT + ROW_HEIGHT / 2;

                      // Simple path: horizontal out, vertical, horizontal in
                      const midX = (x1 + x2) / 2;
                      const path = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;

                      return (
                        <path
                          key={dep.id}
                          d={path}
                          fill="none"
                          stroke="#868e96"
                          strokeWidth={1.5}
                          markerEnd="url(#gantt-arrow)"
                          opacity={0.6}
                        />
                      );
                    })}
                </svg>
              )}
            </Box>
          </Box>
        </ScrollArea>
      </Group>
    </Card>
  );
}
